import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { PropsWithChildren } from "react";
import { useMovementRegistry, type UseMovementRegistryResult } from "../hooks/useMovementRegistry";

export interface MovementRegistryContextValue extends UseMovementRegistryResult {
  /**
   * Trigger the underlying Firestore subscription. The provider stays idle
   * (no listener, zero reads) until the first consumer calls `enable()`.
   * Subsequent calls are no-ops. Safe to call from a `useEffect` on mount or
   * from an event handler.
   */
  enable: () => void;
}

const MovementRegistryContext = createContext<MovementRegistryContextValue | null>(null);

// One subscription shared across all descendants — wrap the dashboard shell
// once so each consumer reads from a single Firestore listener.
//
// The provider stays idle on mount and does NOT open the listener until a
// consumer calls `enable()` (e.g. when the user opens the movement search
// palette, drills into a radar axis, or navigates to a movement detail page).
// The registry collection is ~127 docs, so keeping it lazy avoids loading it
// on dashboards that never drill into a movement.
export function MovementRegistryProvider({ children }: PropsWithChildren) {
  const [enabled, setEnabled] = useState(false);
  const registry = useMovementRegistry({ enabled });

  const enable = useCallback(() => setEnabled(true), []);

  const value = useMemo<MovementRegistryContextValue>(
    () => ({ ...registry, enable }),
    [registry, enable]
  );

  return (
    <MovementRegistryContext.Provider value={value}>{children}</MovementRegistryContext.Provider>
  );
}

/** Null outside a provider — for widgets that can render without the registry. */
export function useOptionalMovementRegistry(): MovementRegistryContextValue | null {
  return useContext(MovementRegistryContext);
}

export function useMovementRegistryFromContext(): MovementRegistryContextValue {
  const ctx = useContext(MovementRegistryContext);
  if (!ctx) {
    throw new Error("useMovementRegistryFromContext must be used inside MovementRegistryProvider");
  }
  return ctx;
}
